import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, Box, Button, capitalize, CircularProgress, Collapse, IconButton, LinearProgress, Link, Slider, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { KeyboardArrowDown, KeyboardArrowLeft, KeyboardArrowRight, KeyboardArrowUp, Person } from "@mui/icons-material";
import { getNeonColor, scrollable } from "../../theme";
import { useReferentialContext } from "../../Context";
import { usePlayerLinkQueue } from "../../playerLinkQueue";
import { useThemeContext } from "../../themeContext";
import { SCALE_STOPS, scaleColor as colorForScale, scaleGlowSx, shadeHex } from "../../settings/numberScale";
import { Player, SoloQAccounts, Agent, Maps } from "../../reckon-api";
import { NeonCard, NeonCardContent, NeonCardHeader } from "../../NeonComponents/NeonCards";
import {
    NeonEmptyState,
    NeonTableCell,
    NeonTableContainer,
    NeonTableHeader,
    NeonTableHeaderCell,
    NeonTableRow,
} from "../../NeonComponents/NeonTable";
import formatters from "../../NeonComponents/Formatters";
import TeamIcon from "../../components/Icons/TeamIcon";
import VlrIcon from "../../components/Icons/VlrIcon";
import TrackerIcon from "../../components/Icons/TrackerIcon";
import NationalityFlag, { nationalityFlagCode } from "../../components/Icons/NationalityFlag";
import FadedAgentArt from "../../components/Icons/FadedAgentArt";
import PlayedAgents, { playedAgentsColumnWidth } from "../../components/Icons/PlayedAgents";
import RankIcon, { findCompetitiveTier, fullRankFromNumber, rankLabel, rankPalette } from "../../components/Icons/RankIcon";
import RoleIcon from "../../components/Icons/RoleIcon";
import AgentIcon from "../../components/Icons/AgentIcon";
import { agentAccent, agentRowSx, hexRgba } from "../../components/Icons/agentColors";
import WindowContainer from "../../components/layout/WindowContainer";
import { getMapAsset } from "../../valorant-api/mapAssets";

const PAGE_SIZE = 50;
const REGIONS = ["eu", "na", "ap", "kr", "latam", "br"];
const RANK_BANDS = [
    { id: "radiant", label: "Radiant", min: 27, max: null as number | null },
    { id: "immortal", label: "Immortal", min: 24, max: 27 },
    { id: "ascendant", label: "Ascendant", min: 21, max: 24 },
];
const ROLE_FILTERS = ["Duelist", "Initiator", "Controller", "Sentinel"];
const MIN_GAMES_MAX = 100;
const FILTER_LABEL_SX = {
    fontSize: "0.62rem",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase" as const,
    color: "rgba(255,255,255,0.42)",
    mb: 0.9,
};
const FILTER_PANEL_SX = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 0.5,
    p: 0.5,
    borderRadius: 1,
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
};

const STAT_AGGREGATES = [
    "win__avg",
    "pk__cnt",
    "acs__avg",
    "adr__avg",
    "kast__avg",
    "rating_vlr__avg",
    "headshot_percent__avg",
    "kills__avg",
    "deaths__avg",
];

interface BreakdownStat {
    id: string;
    games: number;
    winRate: number;
    acs: number;
    kd: number;
    adr: number;
    rating: number;
}

function unwrapId(value: any): string {
    if (value == null || value === "") return "";
    if (typeof value === "object") return str(value, "id", "role", "name", "pk");
    return String(value);
}

function fromBreakdownRow(row: any, groupField: string): BreakdownStat | null {
    const leaf = groupField.includes("__") ? groupField.split("__").pop() : "";
    const id = unwrapId(row?.[groupField]) || (leaf ? unwrapId(row?.[leaf]) : "") || unwrapId(row?.pk) || unwrapId(row?.id);
    if (!id) return null;
    const kills = num(row, "kills__avg", "kills");
    const deaths = num(row, "deaths__avg", "deaths");
    return {
        id,
        games: num(row, "games", "pk__cnt", "pk__count", "game_id__cnt"),
        winRate: hsUnit(num(row, "win__avg", "win")),
        acs: num(row, "acs__avg", "acs"),
        kd: deaths ? kills / deaths : kills,
        adr: num(row, "adr__avg", "adr"),
        rating: num(row, "rating_vlr__avg", "rating_vlr", "rating"),
    };
}

interface MapRateStat {
    rate: number | null;
    wins: number | null;
    losses: number | null;
}

interface MapStatsRow {
    map: string;
    games: number;
    wins: number;
    losses: number;
    winRate: number;
    round: MapRateStat;
    pistol: MapRateStat;
    atk: MapRateStat;
    def: MapRateStat;
}

const EMPTY_MAP_RATE: MapRateStat = { rate: null, wins: null, losses: null };

function rateFromRecord(wins: number, losses: number): MapRateStat {
    const total = wins + losses;
    if (total <= 0) return EMPTY_MAP_RATE;
    return { rate: wins / total, wins, losses };
}

function mapArtUrl(mapId: string, mapRef?: Maps) {
    if (mapRef?.splash) return mapRef.splash;
    if (mapRef?.uuid) return `https://media.valorant-api.com/maps/${mapRef.uuid}/splash.png`;
    return getMapAsset(mapId, "splash").replace("/upload/", "/upload/c_fill,h_360,w_1440,q_auto/");
}

function mapBannerUrl(mapId: string, mapRef?: Maps) {
    if (mapRef?.list_view_icon) return mapRef.list_view_icon;
    if (mapRef?.uuid) return `https://media.valorant-api.com/maps/${mapRef.uuid}/listviewicon.png`;
    return getMapAsset(mapId, "listViewIcon").replace("/upload/", "/upload/c_fill,h_72,w_200/");
}

function mapRateStatsFromMetric(rows: any[], rateKey: string, countKeys: string[] = []): Map<string, MapRateStat> {
    const out = new Map<string, MapRateStat>();
    if (!rateKey) return out;
    for (const row of rows) {
        const map = row?.map ?? row?.id;
        const rateRaw = row?.[rateKey] ?? row?.[`${rateKey}__avg`] ?? (rateKey === "win__avg" ? row?.win : undefined);
        if (!map || rateRaw === null || rateRaw === undefined || Number.isNaN(Number(rateRaw))) continue;
        let rate = Number(rateRaw);
        if (rate > 1) rate = rate / 100;
        const rounds = countKeys.reduce((n, key) => n || Number(row?.[key]) || 0, 0);
        const wins = rounds > 0 ? Math.round(rate * rounds) : null;
        const losses = wins === null ? null : Math.max(0, rounds - wins);
        out.set(String(map).toLowerCase(), { rate, wins, losses });
    }
    return out;
}

function findSideMetric(metrics: { code: string; label?: string | null; _short?: string | null }[], codes: string[], pattern: RegExp) {
    const set = new Set(codes);
    const blob = (metric: { code: string; label?: string | null; _short?: string | null }) =>
        `${metric.code} ${metric.label ?? ""} ${metric._short ?? ""}`.toLowerCase();
    return metrics.find((metric) => set.has(metric.code.toLowerCase())) || metrics.find((metric) => pattern.test(blob(metric)));
}

const PISTOL_METRIC_CODES = [
    "pistol_winrate",
    "pistol_wr",
    "pistol_win",
    "winrate_pistol",
    "pistol_rwr",
    "rwr_pistol",
    "pwr",
];
const PISTOL_METRIC_PATTERN = /pistol.*(win|wr)|win.*pistol|\bpwr\b/;
const PISTOL_COUNT_KEYS = ["pistol_rounds", "pistol_round", "rounds_pistol"];

const expandCache = new Map<string, {
    agents: BreakdownStat[];
    roles: BreakdownStat[];
    maps: MapStatsRow[];
    agentsDone?: boolean;
    rolesDone?: boolean;
    mapsDone?: boolean;
    roundsDone?: boolean;
}>();
const AGENT_TABLE_ROWS = 7;
const EXPAND_AGENT_ROW_HEIGHT = 36;
const EXPAND_TABLE_HEADER_HEIGHT = 26;
const EXPAND_AGENT_TABLE_HEIGHT = EXPAND_TABLE_HEADER_HEIGHT + AGENT_TABLE_ROWS * EXPAND_AGENT_ROW_HEIGHT;
const EXPAND_MAP_ROW_HEIGHT = 148;
const EXPAND_MAP_VISIBLE = 6;
const EXPAND_MAP_GAP = 6;
const EXPAND_BODY_MIN_HEIGHT = EXPAND_AGENT_TABLE_HEIGHT + EXPAND_MAP_ROW_HEIGHT + 56;
const REVEAL_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
const REVEAL_MS = 520;

interface LeaderboardRow {
    puuid: string;
    accountId: number | null;
    playerId: string;
    accountName: string;
    server: string;
    rankTier: number | null;
    rankedRating: number | null;
    agents: string[];
    games: number;
    winRate: number;
    acs: number;
    kd: number;
    adr: number;
    rating: number;
    hs: number;
}

type SortKey = "rank" | "games" | "winRate" | "acs" | "kd" | "adr" | "rating";
type SortDir = "desc" | "asc";

function compareRank(a: LeaderboardRow, b: LeaderboardRow) {
    const tier = (b.rankTier ?? -1) - (a.rankTier ?? -1);
    if (tier) return tier;
    const rr = (b.rankedRating ?? -1) - (a.rankedRating ?? -1);
    if (rr) return rr;
    return b.rating - a.rating;
}

function metricValue(row: LeaderboardRow, key: Exclude<SortKey, "rank">) {
    return row[key] ?? 0;
}

function sortLeaderboard(rows: LeaderboardRow[], key: SortKey, dir: SortDir) {
    const sign = dir === "desc" ? 1 : -1;
    return [...rows].sort((a, b) => {
        const primary = key === "rank" ? compareRank(a, b) : metricValue(b, key) - metricValue(a, key);
        const signed = primary * sign;
        if (signed) return signed;
        return key === "rank" ? 0 : compareRank(a, b);
    });
}

function hsUnit(value: number) {
    return value > 1 ? value / 100 : value;
}

function numOrNull(row: any, ...keys: string[]): number | null {
    for (const key of keys) {
        if (!key) continue;
        const value = row?.[key];
        if (value != null && value !== "" && !Number.isNaN(Number(value))) return Number(value);
    }
    return null;
}

function num(row: any, ...keys: string[]): number {
    return numOrNull(row, ...keys) ?? 0;
}

function str(row: any, ...keys: string[]): string {
    for (const key of keys) {
        const value = row?.[key];
        if (value != null && value !== "") return String(value);
    }
    return "";
}

function asAggregateRows(result: any, groupField: string): any[] {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.results)) return result.results;
    if (result && typeof result === "object") {
        return Object.entries(result).map(([key, value]) => {
            if (value && typeof value === "object") {
                return { [groupField]: (value as any)[groupField] ?? key, ...(value as object) };
            }
            return { [groupField]: key };
        });
    }
    return [];
}

function parseAgents(value: any, limit = 3): string[] {
    if (value == null || value === "") return [];
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
            try {
                return parseAgents(JSON.parse(trimmed), limit);
            } catch {
                return trimmed.split(",").map((name) => name.trim()).filter(Boolean).slice(0, limit);
            }
        }
        return trimmed.split(",").map((name) => name.trim()).filter(Boolean).slice(0, limit);
    }
    if (Array.isArray(value)) {
        const ranked = value
            .map((item) => {
                if (typeof item === "string") return { name: item, count: 0 };
                const name = str(item, "agent", "id", "name", "key");
                const count = num(item, "count", "games", "pk__cnt", "value");
                return name ? { name, count } : null;
            })
            .filter((item): item is { name: string; count: number } => Boolean(item));
        const hasCounts = ranked.some((item) => item.count > 0);
        if (hasCounts) ranked.sort((a, b) => b.count - a.count);
        return ranked.map((item) => item.name).slice(0, limit);
    }
    if (typeof value === "object") {
        return Object.entries(value)
            .map(([key, entry]) => {
                const count = typeof entry === "number" ? entry : num(entry, "count", "games", "pk__cnt", "value");
                return { name: key, count };
            })
            .sort((a, b) => b.count - a.count)
            .map((item) => item.name)
            .filter(Boolean)
            .slice(0, limit);
    }
    return [];
}

function fromAggregateRow(row: any): LeaderboardRow | null {
    const puuid = str(row, "puuid", "pk", "id");
    if (!puuid) return null;
    const kills = num(row, "kills__avg", "kills");
    const deaths = num(row, "deaths__avg", "deaths");
    return {
        puuid,
        accountId: numOrNull(row, "account", "account_id", "account__id", "account__max"),
        playerId: str(row, "account__player_id", "account__player_id__max", "competitive_player", "player_id"),
        accountName: str(row, "account__account_name", "account__account_name__max", "ig_name", "account_name") || "Unknown",
        server: str(row, "account__server", "account__server__max", "server").toLowerCase(),
        rankTier: numOrNull(row, "account__rank_tier", "account__rank_tier__max", "rank_tier"),
        rankedRating: numOrNull(row, "account__ranked_rating", "account__ranked_rating__max", "ranked_rating"),
        agents: parseAgents(row.agents ?? row.agent),
        games: num(row, "games", "pk__cnt", "pk__count", "game_id__cnt"),
        winRate: hsUnit(num(row, "win__avg", "win")),
        acs: num(row, "acs__avg", "acs"),
        kd: deaths ? kills / deaths : kills,
        adr: num(row, "adr__avg", "adr"),
        rating: num(row, "rating_vlr__avg", "rating_vlr", "rating"),
        hs: hsUnit(num(row, "headshot_percent__avg", "headshot_percent")),
    };
}

function asAccountList(result: any): SoloQAccounts[] {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.results)) return result.results;
    return [];
}

function applyAccount(row: LeaderboardRow, account?: SoloQAccounts): LeaderboardRow {
    if (!account) return row;
    return {
        ...row,
        accountId: account.id ?? row.accountId,
        playerId: account.player || row.playerId,
        accountName: account.account_name || row.accountName,
        server: (account.server || row.server || "").toLowerCase(),
        rankTier: account.rank_tier ?? row.rankTier,
        rankedRating: account.ranked_rating ?? row.rankedRating,
    };
}

function formatRoleName(role?: string | null) {
    if (!role) return "";
    const name = role.includes("::") ? role.split("::").pop() || role : role;
    return name.replace(/_/g, " ");
}

function mostPlayedRole(agentNames: string[], agents: Agent[]) {
    const main = agentNames[0];
    if (!main) return undefined;
    const agent = agents.find((entry) => entry.id.toLowerCase() === main.toLowerCase());
    const role = formatRoleName(agent?.role);
    if (!role) return undefined;
    return { role, icon: agent?.role_icon };
}

function toggleFilterValue(list: string[], value: string) {
    return list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];
}

function agentsMatchingRoles(agents: Agent[], roles: string[]) {
    const roleSet = new Set(roles.map((role) => role.toLowerCase()));
    return agents.filter((agent) => roleSet.has(formatRoleName(agent.role).toLowerCase()));
}

function resolvedAgentFilter(selectedAgents: string[], selectedRoles: string[], agents: Agent[]) {
    if (selectedRoles.length && agents.length === 0) return undefined;
    const fromRoles = selectedRoles.length ? agentsMatchingRoles(agents, selectedRoles).map((agent) => agent.id) : null;
    if (selectedAgents.length && fromRoles) {
        const allowed = new Set(fromRoles.map((id) => id.toLowerCase()));
        return selectedAgents.filter((id) => allowed.has(id.toLowerCase()));
    }
    if (selectedAgents.length) return selectedAgents;
    if (fromRoles) return fromRoles;
    return null;
}

function normalizeAgentKey(name: string) {
    return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

const AGENT_UUID: Record<string, string> = {
    astra: "41fb69c1-4189-7b37-f117-bcaf1e96f1bf",
    breach: "5f8d3a7f-467b-97f3-062c-13acf203c006",
    brimstone: "9f0d8ba9-4140-b941-57d3-a7ad57c6b417",
    chamber: "22697a3d-45bf-8dd7-4fec-84a9e28c69d7",
    clove: "1dbf2edd-4729-0984-3115-daa5eed44993",
    cypher: "117ed9e3-49f3-6512-3ccf-0cada7e3823b",
    deadlock: "cc8b64c8-4b25-4ff9-6e7f-37b4da43d235",
    fade: "dade69b4-4f5a-8528-247b-219e5a1facd6",
    gekko: "e370fa57-4757-3604-3648-499e1f642d3f",
    harbor: "95b78ed7-4637-86d9-7e41-71ba8c293152",
    iso: "0e38b510-41a8-5780-5e8f-568b2a4f2d6c",
    jett: "add6443a-41bd-e414-f6ad-e58d267f4e95",
    kayo: "601dbbe7-43ce-be57-2a40-4abd24953621",
    killjoy: "1e58de9c-4950-5125-93e9-a0aee9f98746",
    miks: "7c8a4701-4de6-9355-b254-e09bc2a34b72",
    neon: "bb2a4828-46eb-8cd1-e765-15848195d751",
    omen: "8e253930-4c05-31dd-1b6c-968525494517",
    phoenix: "eb93336a-449b-9c1b-0a54-a891f7921d69",
    raze: "f94c3b30-42be-e959-889c-5aa313dba261",
    reyna: "a3bfb853-43b2-7238-a4f1-ad90e9e46bcc",
    sage: "569fdd95-4d10-43ab-ca70-79becc718b46",
    skye: "6f2a04ca-43e0-be17-7f36-b3908627744d",
    sova: "320b2a48-4d9b-a075-30f1-1f93a9b638fa",
    tejo: "b444168c-4e35-8076-db47-ef9bf368f384",
    veto: "92eeef5d-43b5-1d4a-8d03-b3927a09034b",
    viper: "707eab51-4836-f488-046a-cda6bf494859",
    vyse: "efba5359-4016-a1e5-7626-b1ae76895940",
    waylay: "df1cb487-4902-002e-5c17-d28e83e78588",
    yoru: "7f94d92c-4234-0a36-9646-3a87eb8b5c89",
};

function portraitUrls(agentName: string | undefined, agents: Agent[]) {
    if (!agentName) return [];
    const key = normalizeAgentKey(agentName);
    const ref = agents.find((entry) => normalizeAgentKey(entry.id) === key);
    const uuid = ref?.uuid || AGENT_UUID[key];
    const urls: string[] = [];
    if (uuid) urls.push(`https://media.valorant-api.com/agents/${uuid}/fullportrait.png`);
    if (ref?.full_portrait) urls.push(ref.full_portrait);
    return urls.filter((url, index, list) => list.indexOf(url) === index);
}

function FadeLoadedImg({
    src,
    alt = "",
    onError,
    sx,
}: {
    src: string;
    alt?: string;
    onError?: () => void;
    sx?: object;
}) {
    const [visible, setVisible] = useState(false);

    return (
        <Box
            component="img"
            src={src}
            alt={alt}
            onLoad={() => setVisible(true)}
            onError={onError}
            sx={{
                opacity: visible ? 1 : 0,
                transition: `opacity ${REVEAL_MS}ms ${REVEAL_EASE}`,
                ...sx,
            }}
        />
    );
}

function AgentExpandPortrait({ agent, footer }: { agent?: string; footer?: React.ReactNode }) {
    const ctx = useReferentialContext();
    const urls = portraitUrls(agent, ctx.agentsReferential);
    const [index, setIndex] = useState(0);
    const src = urls[index];

    useEffect(() => {
        setIndex(0);
    }, [agent]);

    return (
        <Box
            sx={{
                position: "relative",
                flexShrink: 0,
                alignSelf: "stretch",
                width: 200,
                minHeight: EXPAND_BODY_MIN_HEIGHT,
                overflow: "hidden",
                pointerEvents: footer ? "auto" : "none",
            }}
        >
            {src ? (
                <FadeLoadedImg
                    key={src}
                    src={src}
                    onError={() => setIndex((prev) => prev + 1)}
                    sx={{
                        position: "absolute",
                        left: "50%",
                        top: 0,
                        height: "100%",
                        width: "auto",
                        maxWidth: "none",
                        transform: "translateX(-50%)",
                        objectFit: "contain",
                        userSelect: "none",
                        pointerEvents: "none",
                    }}
                />
            ) : null}
            {footer ? (
                <Box
                    sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 2,
                        pt: 4,
                        pb: 1,
                        background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.72) 70%)",
                    }}
                    onClick={(event) => event.stopPropagation()}
                >
                    {footer}
                </Box>
            ) : null}
        </Box>
    );
}

function Reveal({ show, children }: { show: boolean; children: React.ReactNode }) {
    return (
        <Box
            sx={{
                width: "100%",
                "@keyframes lbReveal": {
                    from: { opacity: 0, transform: "translateY(12px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                },
                animation: show ? `lbReveal ${REVEAL_MS}ms ${REVEAL_EASE} both` : "none",
            }}
        >
            {show ? children : null}
        </Box>
    );
}

function uniqueByPuuid(rows: LeaderboardRow[]): LeaderboardRow[] {
    const byPuuid = new Map<string, LeaderboardRow>();
    for (const row of rows) {
        if (!byPuuid.has(row.puuid)) byPuuid.set(row.puuid, row);
    }
    return Array.from(byPuuid.values());
}

const RANK_ICON_SIZE = 32;
const ROLE_ICON_SIZE = 44;
const ROLE_ZOOM = 1.4;
const ROLE_IMG_SIZE = Math.round(ROLE_ICON_SIZE * ROLE_ZOOM);
const ROLE_VISIBLE_RATIO = 0.9;
const ROLE_COLUMN_WIDTH = Math.round(ROLE_ICON_SIZE * ROLE_VISIBLE_RATIO);
const ROLE_CRANK = 4;
const FLAG_ART_WIDTH = 104;
const FLAG_LAYOUT_WIDTH = FLAG_ART_WIDTH + 8;
const RANK_LEFT_DISSOLVE =
    "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.12) 16%, rgba(0,0,0,0.4) 32%, rgba(0,0,0,0.78) 50%, #000 68%, #000 100%)";
const ROLE_SCRIM =
    "radial-gradient(ellipse 90% 80% at 28% 50%, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.32) 46%, transparent 74%)";
const AGENTS_COLUMN_WIDTH = playedAgentsColumnWidth(3);
const ROW_PLAYER_ORG_SIZE = 26;
const PLAYER_ORGS_SLOT_WIDTH = ROW_PLAYER_ORG_SIZE;

function mixHexOnBlack(hex: string, alpha: number) {
    const n = hex.replace("#", "");
    const r = Math.round(parseInt(n.slice(0, 2), 16) * alpha);
    const g = Math.round(parseInt(n.slice(2, 4), 16) * alpha);
    const b = Math.round(parseInt(n.slice(4, 6), 16) * alpha);
    return `rgb(${r}, ${g}, ${b})`;
}

function fadeSeed(value: string) {
    let hash = 2166136261;
    for (let i = 0; i < value.length; i += 1) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
}

function fadeUnit(seed: number, salt: number) {
    const x = Math.sin(seed * 0.0007 + salt * 12.9898) * 43758.5453;
    return x - Math.floor(x);
}

function flagFade(accent: string | undefined, seed: number) {
    const tint = accent || "#8a93a6";
    const row = accent ? mixHexOnBlack(accent, 0.22) : "rgb(12, 14, 18)";
    const angle = 74 + fadeUnit(seed, 1) * 32;
    const mid = 24 + fadeUnit(seed, 2) * 18;
    const late = 50 + fadeUnit(seed, 3) * 20;
    const radialX = 72 + fadeUnit(seed, 4) * 22;
    const radialY = 18 + fadeUnit(seed, 5) * 64;
    const radialSize = 70 + fadeUnit(seed, 6) * 36;
    return [
        `linear-gradient(${angle.toFixed(1)}deg, ${hexRgba(tint, 0.5)} 0%, ${hexRgba(tint, 0.1)} ${mid.toFixed(0)}%, ${hexRgba(tint, 0.26)} ${late.toFixed(0)}%, ${row} 100%)`,
        `radial-gradient(ellipse ${radialSize.toFixed(0)}% 130% at ${radialX.toFixed(0)}% ${radialY.toFixed(0)}%, transparent 8%, ${row} 78%)`,
    ].join(", ");
}

function flagObjectPosition(seed: number) {
    const x = 28 + fadeUnit(seed, 7) * 44;
    const y = 30 + fadeUnit(seed, 8) * 40;
    return `${x.toFixed(0)}% ${y.toFixed(0)}%`;
}

function RoleMark({ role, icon }: { role?: string; icon?: string | null }) {
    if (!role && !icon) return null;
    return (
        <RoleIcon
            role={role}
            icon={icon}
            size={ROLE_IMG_SIZE}
            sx={{
                position: "absolute",
                left: ROLE_COLUMN_WIDTH - ROLE_IMG_SIZE - ROLE_CRANK,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                filter:
                    "brightness(1.28) drop-shadow(0 0 6px rgba(0,0,0,0.95)) drop-shadow(0 1px 2px rgba(0,0,0,0.9))",
            }}
        />
    );
}

function PlayedRole({
    role,
    icon,
    nationality,
    accent,
    seed,
}: {
    role?: string;
    icon?: string | null;
    nationality?: string | null;
    accent?: string;
    seed?: string;
}) {
    const flagCode = nationalityFlagCode(nationality);
    const fadeKey = fadeSeed(`${seed || ""}:${flagCode || ""}:${accent || ""}`);
    if (!role && !icon && !flagCode) return null;
    return (
        <Box
            sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: flagCode ? FLAG_ART_WIDTH : ROLE_COLUMN_WIDTH + 16,
                overflow: "visible",
                pointerEvents: "none",
                zIndex: 0,
                isolation: "isolate",
            }}
        >
            {flagCode ? (
                <Box
                    sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: FLAG_ART_WIDTH,
                        overflow: "hidden",
                        zIndex: 0,
                    }}
                >
                    <Box
                        component="img"
                        alt=""
                        src={`https://flagcdn.com/w320/${flagCode}.png`}
                        srcSet={`https://flagcdn.com/w160/${flagCode}.png 1x, https://flagcdn.com/w320/${flagCode}.png 2x, https://flagcdn.com/w640/${flagCode}.png 3x`}
                        sx={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: flagObjectPosition(fadeKey),
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            background: flagFade(accent, fadeKey),
                        }}
                    />
                </Box>
            ) : null}
            {flagCode ? (
                <Box
                    sx={{
                        position: "absolute",
                        left: ROLE_COLUMN_WIDTH - ROLE_IMG_SIZE - ROLE_CRANK,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: ROLE_IMG_SIZE + 20,
                        height: ROLE_IMG_SIZE + 12,
                        zIndex: 1,
                        background: ROLE_SCRIM,
                    }}
                />
            ) : null}
            <RoleMark role={role} icon={icon} />
        </Box>
    );
}

function useScale() {
    const { numberScale } = useThemeContext();
    return {
        color: (value: number | null, stops: number[]) => colorForScale(value, stops, numberScale),
        glow: (color: string) => scaleGlowSx(color, numberScale.glow),
        glowAmount: numberScale.glow,
    };
}

function SortHeader({
    id,
    label,
    width,
    minWidth,
    flex,
    align,
    sortKey,
    sortDir,
    onSort,
}: {
    id: SortKey;
    label: string;
    width?: string;
    minWidth?: string;
    flex?: number;
    align?: "left" | "center" | "right";
    sortKey: SortKey;
    sortDir: SortDir;
    onSort: (key: SortKey) => void;
}) {
    const active = sortKey === id;
    const Arrow = sortDir === "asc" ? KeyboardArrowUp : KeyboardArrowDown;
    return (
        <NeonTableHeaderCell
            width={width}
            minWidth={minWidth}
            flex={flex}
            align={align}
            active={active}
            onClick={() => onSort(id)}
        >
            <Box
                component="span"
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: align === "left" ? "flex-start" : "center",
                    gap: 0.15,
                    width: "100%",
                }}
            >
                {label}
                {active ? <Arrow sx={{ fontSize: 14, mb: "-1px", opacity: 0.9 }} /> : null}
            </Box>
        </NeonTableHeaderCell>
    );
}

function ExpandStatsTable({
    rows,
    kind,
    fixedRows,
}: {
    rows: BreakdownStat[];
    kind: "agent" | "role";
    fixedRows?: number;
}) {
    const ctx = useReferentialContext();
    const scale = useScale();
    const headerSx = {
        fontSize: "0.58rem",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase" as const,
        color: "rgba(255,255,255,0.38)",
        textAlign: "center" as const,
        py: 0.5,
        px: 0.4,
        height: EXPAND_TABLE_HEADER_HEIGHT,
    };
    const cellSx = {
        py: 0.45,
        px: 0.4,
        fontSize: "0.72rem",
        fontWeight: 600,
        textAlign: "center" as const,
        fontVariantNumeric: "tabular-nums" as const,
        borderTop: "1px solid rgba(255,255,255,0.06)",
        height: fixedRows ? EXPAND_AGENT_ROW_HEIGHT : undefined,
    };
    const displayRows: (BreakdownStat | null)[] = fixedRows
        ? [...rows.slice(0, fixedRows), ...Array.from({ length: Math.max(0, fixedRows - rows.length) }, () => null)]
        : rows;

    return (
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <Box component="thead">
                <Box component="tr">
                    <Box component="th" sx={{ ...headerSx, textAlign: "left", pl: 0.5 }}>
                        {kind === "agent" ? "Agent" : "Role"}
                    </Box>
                    <Box component="th" sx={headerSx}>G</Box>
                    <Box component="th" sx={headerSx}>WR</Box>
                    <Box component="th" sx={headerSx}>ACS</Box>
                    <Box component="th" sx={headerSx}>KD</Box>
                    <Box component="th" sx={headerSx}>ADR</Box>
                    <Box component="th" sx={headerSx}>Rating</Box>
                </Box>
            </Box>
            <Box component="tbody">
                {displayRows.map((row, index) => {
                    if (!row) {
                        return (
                            <Box component="tr" key={`empty-${index}`}>
                                {Array.from({ length: 7 }).map((_, cell) => (
                                    <Box component="td" key={cell} sx={cellSx} />
                                ))}
                            </Box>
                        );
                    }
                    const wrColor = scale.color(row.winRate, SCALE_STOPS.rate);
                    const acsColor = scale.color(row.acs, SCALE_STOPS.acs);
                    const kdColor = scale.color(row.kd, SCALE_STOPS.kd);
                    const adrColor = scale.color(row.adr, SCALE_STOPS.adr);
                    const ratingColor = scale.color(row.rating || null, SCALE_STOPS.rating);
                    const roleIcon = ctx.agentsReferential.find(
                        (agent) => formatRoleName(agent.role).toLowerCase() === row.id.toLowerCase(),
                    )?.role_icon;
                    return (
                        <Box component="tr" key={row.id}>
                            <Box component="td" sx={{ ...cellSx, textAlign: "left", pl: 0.5 }}>
                                <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", minWidth: 0 }}>
                                    {kind === "agent" ? (
                                        <FadedAgentArt agent={row.id} size={22} fade="none" />
                                    ) : (
                                        <RoleIcon role={row.id} icon={roleIcon} size={18} />
                                    )}
                                    <Typography sx={{ fontWeight: 700, fontSize: "0.72rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {capitalize(row.id)}
                                    </Typography>
                                </Stack>
                            </Box>
                            <Box component="td" sx={cellSx}>{row.games}</Box>
                            <Box component="td" sx={{ ...cellSx, ...scale.glow(wrColor) }}>{formatters.percent(row.winRate)}</Box>
                            <Box component="td" sx={{ ...cellSx, ...scale.glow(acsColor) }}>{formatters.integer(row.acs)}</Box>
                            <Box component="td" sx={{ ...cellSx, ...scale.glow(kdColor) }}>{formatters.float2(row.kd)}</Box>
                            <Box component="td" sx={{ ...cellSx, ...scale.glow(adrColor) }}>{formatters.integer(row.adr)}</Box>
                            <Box component="td" sx={{ ...cellSx, ...scale.glow(ratingColor) }}>
                                {row.rating ? formatters.float2(row.rating) : "—"}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

function MapStatCard({ stats, mapRef }: { stats: MapStatsRow; mapRef?: Maps }) {
    const scale = useScale();
    const [imgSrc, setImgSrc] = useState(mapArtUrl(stats.map, mapRef));
    const wrColor = scale.color(stats.winRate, SCALE_STOPS.rate);
    const tint = wrColor.startsWith("#") ? wrColor : null;
    const darkTint = tint ? shadeHex(tint, 0.42) : null;

    useEffect(() => {
        setImgSrc(mapArtUrl(stats.map, mapRef));
    }, [stats.map, mapRef?.uuid, mapRef?.splash]);

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                minHeight: 148,
                minWidth: 0,
                borderRadius: 1,
                overflow: "hidden",
                border: tint ? `1px solid ${hexRgba(tint, 0.32)}` : "1px solid rgba(255,255,255,0.12)",
                backgroundColor: "rgba(255,255,255,0.03)",
            }}
        >
            <FadeLoadedImg
                key={imgSrc}
                src={imgSrc}
                alt={stats.map}
                onError={() => setImgSrc(mapBannerUrl(stats.map, mapRef))}
                sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center",
                }}
            />
            {darkTint ? (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: darkTint,
                        mixBlendMode: "multiply",
                        opacity: 0.82,
                        pointerEvents: "none",
                    }}
                />
            ) : null}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: tint
                        ? `linear-gradient(180deg, ${hexRgba(tint, 0.22)} 0%, rgba(4,6,10,0.58) 38%, rgba(4,6,10,0.86) 68%, rgba(4,6,10,0.96) 100%)`
                        : "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.45) 35%, rgba(0,0,0,0.82) 70%, rgba(0,0,0,0.96) 100%)",
                    pointerEvents: "none",
                }}
            />
            <Stack sx={{ position: "relative", zIndex: 1, height: "100%", justifyContent: "flex-end", p: 0.75, gap: 0.3 }}>
                <Typography
                    sx={{
                        fontWeight: 800,
                        fontSize: "0.78rem",
                        textTransform: "capitalize",
                        letterSpacing: 0.15,
                        lineHeight: 1.1,
                        textShadow: "0 1px 8px rgba(0,0,0,0.9)",
                    }}
                >
                    {stats.map}
                </Typography>
                {(
                    [
                        ["Games", { rate: stats.winRate, wins: stats.wins, losses: stats.losses }],
                        ["Round", stats.round],
                        ["Pistol", stats.pistol],
                        ["Atk", stats.atk],
                        ["Def", stats.def],
                    ] as const
                ).map(([label, detail]) => {
                    const rate = detail.rate;
                    const color = scale.color(rate, SCALE_STOPS.rate);
                    const record =
                        detail.wins !== null && detail.losses !== null ? `${detail.wins}W–${detail.losses}L` : "—";
                    return (
                        <Box key={label}>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "40px minmax(0, 1fr) 36px",
                                    columnGap: 0.5,
                                    alignItems: "center",
                                }}
                            >
                                <Typography sx={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>
                                    {label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "0.62rem",
                                        color: "rgba(255,255,255,0.55)",
                                        fontWeight: 600,
                                        fontVariantNumeric: "tabular-nums",
                                        textAlign: "right",
                                    }}
                                >
                                    {record}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: "0.68rem",
                                        ...(rate === null ? { color, fontVariantNumeric: "tabular-nums" } : scale.glow(color)),
                                        textAlign: "right",
                                    }}
                                >
                                    {rate === null ? "—" : formatters.percent(rate)}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={rate === null ? 0 : Math.max(rate * 100, 4)}
                                sx={{
                                    mt: 0.15,
                                    height: 3,
                                    borderRadius: 2,
                                    backgroundColor: "rgba(255,255,255,0.12)",
                                    "& .MuiLinearProgress-bar": {
                                        borderRadius: 2,
                                        backgroundColor: color,
                                        boxShadow: rate === null || !scale.glowAmount ? "none" : `0 0 8px ${color}88`,
                                    },
                                }}
                            />
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
}

function MapStatsStrip({
    maps,
    ready,
    mapsById,
}: {
    maps: MapStatsRow[];
    ready: boolean;
    mapsById: Map<string, Maps>;
}) {
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);
    const cardWidth = `calc((100% - ${(EXPAND_MAP_VISIBLE - 1) * EXPAND_MAP_GAP}px) / ${EXPAND_MAP_VISIBLE})`;
    const fillRow = ready && maps.length > 0 && maps.length <= EXPAND_MAP_VISIBLE;
    const itemFlex = fillRow ? "1 1 0" : `0 0 ${cardWidth}`;

    const syncChevrons = () => {
        const el = scrollerRef.current;
        if (!el) {
            setCanLeft(false);
            setCanRight(false);
            return;
        }
        setCanLeft(el.scrollLeft > 2);
        setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    };

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;
        syncChevrons();
        el.addEventListener("scroll", syncChevrons, { passive: true });
        const observer = new ResizeObserver(syncChevrons);
        observer.observe(el);
        return () => {
            el.removeEventListener("scroll", syncChevrons);
            observer.disconnect();
        };
    }, [ready, maps.length]);

    const mapIds = maps.map((row) => row.map).join(",");
    useEffect(() => {
        scrollerRef.current?.scrollTo({ left: 0 });
        syncChevrons();
    }, [mapIds]);

    const scrollByCard = (direction: -1 | 1) => {
        const el = scrollerRef.current;
        if (!el) return;
        const card = el.firstElementChild as HTMLElement | null;
        const step = (card?.getBoundingClientRect().width || 140) + EXPAND_MAP_GAP;
        el.scrollBy({ left: direction * step, behavior: "smooth" });
    };

    const chevronSx = {
        color: "rgba(255,255,255,0.72)",
        flexShrink: 0,
        "&.Mui-disabled": { color: "rgba(255,255,255,0.18)" },
    };

    if (ready && !maps.length) {
        return (
            <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.35)", py: 1 }}>
                No map stats.
            </Typography>
        );
    }

    return (
        <Stack direction="row" spacing={0.25} sx={{ alignItems: "center", minWidth: 0, width: "100%" }}>
            <IconButton
                size="small"
                aria-label="Previous maps"
                disabled={!canLeft}
                onClick={(event) => {
                    event.stopPropagation();
                    scrollByCard(-1);
                }}
                sx={chevronSx}
            >
                <KeyboardArrowLeft />
            </IconButton>
            <Box
                ref={scrollerRef}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    gap: `${EXPAND_MAP_GAP}px`,
                    overflow: "hidden",
                    height: EXPAND_MAP_ROW_HEIGHT,
                    scrollBehavior: "smooth",
                }}
            >
                {ready
                    ? maps.map((stats, index) => (
                        <Box
                            key={stats.map}
                            sx={{
                                flex: itemFlex,
                                width: fillRow ? undefined : cardWidth,
                                minWidth: 0,
                                height: EXPAND_MAP_ROW_HEIGHT,
                                "@keyframes lbMapIn": {
                                    from: { opacity: 0, transform: "translateY(8px)" },
                                    to: { opacity: 1, transform: "translateY(0)" },
                                },
                                animation: `lbMapIn 420ms ${REVEAL_EASE} ${Math.min(index, 8) * 45}ms both`,
                            }}
                        >
                            <MapStatCard stats={stats} mapRef={mapsById.get(stats.map.toLowerCase())} />
                        </Box>
                    ))
                    : Array.from({ length: EXPAND_MAP_VISIBLE }, (_, index) => (
                        <Box
                            key={index}
                            sx={{
                                flex: `0 0 ${cardWidth}`,
                                width: cardWidth,
                                minWidth: 0,
                                height: EXPAND_MAP_ROW_HEIGHT,
                                visibility: "hidden",
                            }}
                        />
                    ))}
            </Box>
            <IconButton
                size="small"
                aria-label="Next maps"
                disabled={!canRight}
                onClick={(event) => {
                    event.stopPropagation();
                    scrollByCard(1);
                }}
                sx={chevronSx}
            >
                <KeyboardArrowRight />
            </IconButton>
        </Stack>
    );
}

function parseBreakdown(result: any, groupField: string, formatId?: (id: string) => string): BreakdownStat[] {
    const byId = new Map<string, BreakdownStat & { weight: number }>();
    for (const row of asAggregateRows(result, groupField)) {
        const parsed = fromBreakdownRow(row, groupField);
        if (!parsed) continue;
        const id = formatId ? formatId(parsed.id) : parsed.id;
        if (!id || id.toLowerCase() === "unknown") continue;
        const key = id.toLowerCase();
        const weight = parsed.games || 1;
        const prev = byId.get(key);
        if (!prev) {
            byId.set(key, { ...parsed, id, weight });
            continue;
        }
        const total = prev.weight + weight;
        prev.games += parsed.games;
        prev.winRate = (prev.winRate * prev.weight + parsed.winRate * weight) / total;
        prev.acs = (prev.acs * prev.weight + parsed.acs * weight) / total;
        prev.kd = (prev.kd * prev.weight + parsed.kd * weight) / total;
        prev.adr = (prev.adr * prev.weight + parsed.adr * weight) / total;
        prev.rating = (prev.rating * prev.weight + parsed.rating * weight) / total;
        prev.weight = total;
    }
    return Array.from(byId.values())
        .map(({ weight: _weight, ...row }) => row)
        .sort((a, b) => b.games - a.games || b.rating - a.rating);
}

function mapsFromAggregate(result: any): MapStatsRow[] {
    return asAggregateRows(result, "map")
        .map((row) => {
            const id = str(row, "map", "pk", "id");
            if (!id) return null;
            const games = num(row, "games", "pk__cnt", "pk__count", "game_id__cnt");
            const winRate = hsUnit(num(row, "win__avg", "win"));
            const wins = games ? Math.round(winRate * games) : 0;
            const roundWins = numOrNull(row, "rounds_won__sum", "rounds_won");
            const roundLosses = numOrNull(row, "rounds_lost__sum", "rounds_lost");
            return {
                map: id,
                games,
                wins,
                losses: Math.max(0, games - wins),
                winRate,
                round: roundWins != null && roundLosses != null ? rateFromRecord(roundWins, roundLosses) : EMPTY_MAP_RATE,
                pistol: EMPTY_MAP_RATE,
                atk: EMPTY_MAP_RATE,
                def: EMPTY_MAP_RATE,
            } as MapStatsRow;
        })
        .filter((row): row is MapStatsRow => Boolean(row))
        .sort((a, b) => b.games - a.games || b.winRate - a.winRate);
}

function applyRoundRates(
    maps: MapStatsRow[],
    pistol: Map<string, MapRateStat>,
    atk: Map<string, MapRateStat>,
    def: Map<string, MapRateStat>,
): MapStatsRow[] {
    return maps.map((row) => {
        const key = row.map.toLowerCase();
        return {
            ...row,
            pistol: pistol.get(key) ?? row.pistol,
            atk: atk.get(key) ?? row.atk,
            def: def.get(key) ?? row.def,
        };
    });
}

function normalizePlayerName(value: string) {
    return value.trim().toLowerCase().split("#")[0].replace(/[^a-z0-9]+/g, "");
}

function levenshtein(a: string, b: string) {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;
    const row = Array.from({ length: b.length + 1 }, (_, index) => index);
    for (let i = 1; i <= a.length; i += 1) {
        let prev = i - 1;
        row[0] = i;
        for (let j = 1; j <= b.length; j += 1) {
            const current = row[j];
            row[j] = a[i - 1] === b[j - 1] ? prev : Math.min(prev, row[j], row[j - 1]) + 1;
            prev = current;
        }
    }
    return row[b.length];
}

function parseTeamList(value?: string | null) {
    if (!value) return [];
    return value
        .split(/[,;/|]+/)
        .map((entry) => entry.trim())
        .filter(Boolean);
}

function playerTeamNames(player: Player) {
    const current = player.current_team?.trim() || "";
    const last = player.last_team?.trim() || "";
    const seen = new Set<string>();
    const others: string[] = [];
    for (const name of parseTeamList(player.other_teams)) {
        const key = name.toLowerCase();
        if (!key || key === current.toLowerCase() || key === last.toLowerCase() || seen.has(key)) continue;
        seen.add(key);
        others.push(name);
    }
    return {
        current,
        last: last && last.toLowerCase() !== current.toLowerCase() ? last : "",
        others,
    };
}

function PlayerOrgs({
    player,
    size = 22,
    showFlag = true,
    showLastTeam = true,
    showOtherTeams = true,
}: {
    player: Player;
    size?: number;
    showFlag?: boolean;
    showLastTeam?: boolean;
    showOtherTeams?: boolean;
}) {
    const { current, last, others } = playerTeamNames(player);
    const extra = showOtherTeams ? others.slice(0, 4) : [];
    const lastTeam = showLastTeam ? last : "";
    if ((showFlag ? !player.nationality : true) && !current && !lastTeam && extra.length === 0) return null;
    return (
        <Stack direction="row" spacing={0.4} sx={{ alignItems: "center", flexShrink: 0 }}>
            {showFlag ? <NationalityFlag nationality={player.nationality} height={Math.round(size * 0.64)} /> : null}
            {current ? <TeamIcon team_name={current} sx={{ width: size, height: size }} /> : null}
            {lastTeam ? (
                <TeamIcon
                    team_name={lastTeam}
                    sx={{ width: Math.round(size * 0.86), height: Math.round(size * 0.86), opacity: 0.78 }}
                />
            ) : null}
            {extra.map((team) => (
                <TeamIcon
                    key={team}
                    team_name={team}
                    sx={{ width: Math.round(size * 0.72), height: Math.round(size * 0.72), opacity: 0.62 }}
                />
            ))}
        </Stack>
    );
}

function playerNameCandidates(player: Player) {
    return [player.id, player.previous_names || ""]
        .flatMap((value) => value.split(/[,;/|]/))
        .map(normalizePlayerName)
        .filter((value) => value.length >= 2);
}

function similarNameScore(account: string, candidate: string) {
    if (!account || !candidate) return 0;
    if (account === candidate) return 100;
    const shorter = Math.min(account.length, candidate.length);
    const longer = Math.max(account.length, candidate.length);
    if (shorter >= 3 && (account.startsWith(candidate) || candidate.startsWith(account))) {
        return 70 + (shorter / longer) * 25;
    }
    if (shorter >= 4 && (account.includes(candidate) || candidate.includes(account))) {
        return 55 + shorter;
    }
    if (longer >= 4) {
        const distance = levenshtein(account, candidate);
        if (distance <= 2 && distance / longer <= 0.34) return 45 - distance * 8;
    }
    return 0;
}

function findSimilarPlayer(accountName: string, players: Player[]) {
    const account = normalizePlayerName(accountName);
    if (account.length < 3) return null;
    let best: { player: Player; score: number } | null = null;
    for (const player of players) {
        let score = 0;
        for (const candidate of playerNameCandidates(player)) {
            score = Math.max(score, similarNameScore(account, candidate));
        }
        if (score > 0 && (!best || score > best.score)) best = { player, score };
    }
    return best?.player ?? null;
}

function playerProfileUrl(player: Player | null) {
    if (!player?.vlr_id) return null;
    return `https://www.vlr.gg/player/${player.vlr_id}`;
}

function soloqProfileHref(playerId: string) {
    return `/soloq-player-profile?player=${encodeURIComponent(playerId)}`;
}

function trackerProfileUrl(accountName?: string) {
    const value = (accountName || "").trim();
    if (!value || value === "Unknown" || !value.includes("#")) return null;
    return `https://tracker.gg/valorant/profile/riot/${encodeURIComponent(value)}/overview`;
}

function SplashIconLink({ href, title, children }: { href: string; title: string; children: React.ReactNode }) {
    return (
        <Tooltip title={title}>
            <Link
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => event.stopPropagation()}
                sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "8px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    backgroundColor: "rgba(0,0,0,0.55)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.78)",
                        borderColor: "rgba(255,255,255,0.38)",
                    },
                }}
            >
                {children}
            </Link>
        </Tooltip>
    );
}

function SplashProfileLinks({
    playerId,
    vlrUrl,
    trackerUrl,
}: {
    playerId?: string;
    vlrUrl: string | null;
    trackerUrl: string | null;
}) {
    if (!playerId && !vlrUrl && !trackerUrl) return null;
    return (
        <Stack direction="row" spacing={0.75} sx={{ justifyContent: "center", alignItems: "center" }}>
            {playerId ? (
                <SplashIconLink href={soloqProfileHref(playerId)} title="Player profile">
                    <Person sx={{ fontSize: 20 }} />
                </SplashIconLink>
            ) : null}
            {vlrUrl ? (
                <SplashIconLink href={vlrUrl} title="VLR profile">
                    <VlrIcon size={22} title="" />
                </SplashIconLink>
            ) : null}
            {trackerUrl ? (
                <SplashIconLink href={trackerUrl} title="tracker.gg profile">
                    <TrackerIcon size={22} title="" />
                </SplashIconLink>
            ) : null}
        </Stack>
    );
}

function LinkPlayerSection({
    puuid,
    accountName,
    onLinked,
}: {
    puuid: string;
    accountName: string;
    onLinked: (playerId: string) => void;
}) {
    const ctx = useReferentialContext();
    const { enqueuePlayerLink, isPending } = usePlayerLinkQueue();
    const suggestedPlayer = useMemo(
        () => findSimilarPlayer(accountName, ctx.playersReferential),
        [accountName, ctx.playersReferential],
    );
    const [player, setPlayer] = useState<Player | null>(suggestedPlayer);
    const [error, setError] = useState<string | null>(null);
    const userTouched = useRef(false);
    const isSuggestion = Boolean(player && suggestedPlayer && player.id === suggestedPlayer.id);
    const profileUrl = playerProfileUrl(player);
    const queued = isPending(puuid);

    useEffect(() => {
        if (userTouched.current) return;
        setPlayer(suggestedPlayer);
    }, [suggestedPlayer]);

    const link = () => {
        if (!player || queued) return;
        setError(null);
        const queuedOk = enqueuePlayerLink({
            playerId: player.id,
            puuid,
            accountName,
            onComplete: (ok) => {
                if (!ok) onLinked("");
            },
        });
        if (!queuedOk) {
            setError("This account is already being linked.");
            return;
        }
        onLinked(player.id);
    };

    return (
        <Box onClick={(event) => event.stopPropagation()}>
            <Typography
                sx={{
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.45)",
                    mb: 0.5,
                }}
            >
                Link player
            </Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                <Autocomplete
                    size="small"
                    options={ctx.playersReferential}
                    value={player}
                    onChange={(_event, value) => {
                        userTouched.current = true;
                        setPlayer(value);
                        setError(null);
                    }}
                    disabled={queued}
                    getOptionLabel={(option) => option.id}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    filterOptions={(options, state) => {
                        const query = state.inputValue.trim().toLowerCase();
                        if (query.length < 1) return [];
                        return options.filter((entry) => entry.id.toLowerCase().startsWith(query)).slice(0, 40);
                    }}
                    renderOption={(props, option) => {
                        const suggested = suggestedPlayer?.id === option.id;
                        return (
                            <Box
                                component="li"
                                {...props}
                                key={option.id}
                                sx={suggested ? { color: "#ff8dc7", fontWeight: 700 } : undefined}
                            >
                                <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", minWidth: 0 }}>
                                    <PlayerOrgs player={option} size={18} />
                                    <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {option.id}
                                    </Box>
                                </Stack>
                            </Box>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Player ID"
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: player ? (
                                    <Stack direction="row" spacing={0.4} sx={{ alignItems: "center", mr: 0.5 }}>
                                        {params.InputProps.startAdornment}
                                        <PlayerOrgs player={player} size={18} />
                                    </Stack>
                                ) : (
                                    params.InputProps.startAdornment
                                ),
                            }}
                            sx={{
                                "& .MuiInputBase-root": {
                                    fontSize: "0.78rem",
                                    backgroundColor: "rgba(0,0,0,0.24)",
                                },
                                "& .MuiInputBase-input": {
                                    color: isSuggestion ? "#ff8dc7" : "#fff",
                                    fontWeight: isSuggestion ? 700 : 600,
                                },
                            }}
                        />
                    )}
                    sx={{ minWidth: 280, width: 360 }}
                />
                <Button
                    size="small"
                    disabled={!player || queued}
                    onClick={link}
                    sx={{
                        fontWeight: 700,
                        textTransform: "none",
                        minHeight: 36,
                        px: 1.5,
                        border: "1px solid rgba(255,255,255,0.3)",
                        color: "#fff",
                    }}
                >
                    {queued ? "Queued" : "Link"}
                </Button>
                {profileUrl ? (
                    <Link
                        href={profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            lineHeight: 0,
                            opacity: isSuggestion ? 1 : 0.85,
                            "&:hover": { opacity: 1 },
                        }}
                    >
                        <VlrIcon size={32} title="VLR profile" />
                    </Link>
                ) : null}
            </Stack>
            {error ? (
                <Typography sx={{ fontSize: "0.68rem", color: "#f87171", mt: 0.5 }}>{error}</Typography>
            ) : null}
        </Box>
    );
}

function PlayerBreakdown({
    puuid,
    accountId,
    open,
    playerId,
    accountName,
    mainAgent,
    onLinked,
}: {
    puuid: string;
    accountId?: number | null;
    open: boolean;
    playerId?: string;
    accountName?: string;
    mainAgent?: string;
    onLinked: (playerId: string) => void;
}) {
    const ctx = useReferentialContext();
    const cached = expandCache.get(puuid);
    const [agents, setAgents] = useState<BreakdownStat[]>(cached?.agents ?? []);
    const [roles, setRoles] = useState<BreakdownStat[]>(cached?.roles ?? []);
    const [maps, setMaps] = useState<MapStatsRow[]>(cached?.maps ?? []);
    const [agentsReady, setAgentsReady] = useState(Boolean(cached?.agentsDone));
    const [rolesReady, setRolesReady] = useState(Boolean(cached?.rolesDone));
    const [mapsReady, setMapsReady] = useState(Boolean(cached?.mapsDone));

    const mapsById = useMemo(() => {
        const byId = new Map<string, Maps>();
        for (const map of ctx.mapsReferential) byId.set(map.id.toLowerCase(), map);
        return byId;
    }, [ctx.mapsReferential]);

    const player = useMemo(() => {
        if (!playerId) return null;
        return ctx.playersReferential.find((entry) => entry.id.toLowerCase() === playerId.toLowerCase()) ?? null;
    }, [playerId, ctx.playersReferential]);
    const vlrUrl = playerProfileUrl(player);
    const trackerUrl = trackerProfileUrl(accountName);

    useEffect(() => {
        if (!open) return;
        const hit = expandCache.get(puuid);
        if (hit) {
            if (hit.agentsDone) {
                setAgents(hit.agents);
                setAgentsReady(true);
            }
            if (hit.rolesDone) {
                setRoles(hit.roles);
                setRolesReady(true);
            }
            if (hit.mapsDone) {
                setMaps(hit.maps);
                setMapsReady(true);
            }
            if (hit.agentsDone && hit.rolesDone && hit.mapsDone && hit.roundsDone) return;
        }
        let cancelled = false;
        if (!hit?.agentsDone) setAgentsReady(false);
        if (!hit?.rolesDone) setRolesReady(false);
        if (!hit?.mapsDone) setMapsReady(false);
        const additional_filters = { puuid };
        const gameRequest = (groupby: string) =>
            ctx.reckonApi.soloqGameSummaries.aggregate({
                groupby: groupby as any,
                ordering: ["-pk__cnt"] as any,
                additional_filters,
                aggregates: [[...STAT_AGGREGATES, "rounds_won__sum", "rounds_lost__sum"].join(",")] as any,
            });
        const roundRequest = async (extra: Record<string, any> = {}, metricCodes: string[] = ["rounds"]) => {
            if (accountId == null) return [];
            const request = {
                groupby: "map" as any,
                ordering: ["map"] as any,
                aggregates: ["win__avg"] as any,
                account: accountId,
                additional_filters: { account_id: accountId, ...extra },
                metrics: [metricCodes.join(",")] as any,
            };
            try {
                return asAggregateRows(await ctx.reckonApi.soloQRoundSummaries.aggregate(request), "map");
            } catch {
                try {
                    const { metrics: _metrics, ...rest } = request;
                    return asAggregateRows(await ctx.reckonApi.soloQRoundSummaries.aggregate(rest), "map");
                } catch {
                    return [];
                }
            }
        };

        const persist = (next: Partial<NonNullable<ReturnType<typeof expandCache.get>>>) => {
            const prev = expandCache.get(puuid) || { agents: [], roles: [], maps: [] };
            expandCache.set(puuid, { ...prev, ...next });
        };
        const pendingRates = {
            pistol: new Map<string, MapRateStat>(),
            atk: new Map<string, MapRateStat>(),
            def: new Map<string, MapRateStat>(),
        };
        const withPendingRates = (rows: MapStatsRow[]) =>
            applyRoundRates(rows, pendingRates.pistol, pendingRates.atk, pendingRates.def);
        const patchRates = (patch: Partial<typeof pendingRates>) => {
            Object.assign(pendingRates, patch);
            setMaps((prev) => {
                if (!prev.length) return prev;
                const next = withPendingRates(prev);
                persist({ maps: next });
                return next;
            });
        };

        if (!hit?.agentsDone) {
            gameRequest("agent")
                .catch(() => [])
                .then((agentRows) => {
                    if (cancelled) return;
                    const agentsParsed = parseBreakdown(agentRows, "agent");
                    setAgents(agentsParsed);
                    setAgentsReady(true);
                    persist({ agents: agentsParsed, agentsDone: true });
                });
        }

        if (!hit?.rolesDone) {
            gameRequest("role")
                .catch(() => [])
                .then((roleRows) => {
                    if (cancelled) return;
                    const rolesParsed = parseBreakdown(roleRows, "role", formatRoleName);
                    setRoles(rolesParsed);
                    setRolesReady(true);
                    persist({ roles: rolesParsed, rolesDone: true });
                });
        }

        if (!hit?.mapsDone) {
            gameRequest("map")
                .catch(() => [])
                .then((mapRows) => {
                    if (cancelled) return;
                    const mapsParsed = withPendingRates(mapsFromAggregate(mapRows));
                    setMaps(mapsParsed);
                    setMapsReady(true);
                    persist({ maps: mapsParsed, mapsDone: true });
                });
        }

        if (!hit?.roundsDone) {
        const pistolMetric = findSideMetric(ctx.metricsReferential, PISTOL_METRIC_CODES, PISTOL_METRIC_PATTERN);
        const atkMetric = findSideMetric(
            ctx.metricsReferential,
            ["atk_winrate", "attack_winrate", "atk_wr", "atk_win", "winrate_atk", "attack_wr", "atk_rwr", "attack_rwr", "rwr_atk"],
            /(atk|attack).*(win|wr)|win.*(atk|attack)/,
        );
        const defMetric = findSideMetric(
            ctx.metricsReferential,
            ["def_winrate", "defense_winrate", "defence_winrate", "def_wr", "def_win", "winrate_def", "defense_wr", "def_rwr", "defense_rwr", "rwr_def"],
            /(def|defense|defence).*(win|wr)|win.*(def|defense|defence)/,
        );
        const pistolCode = pistolMetric?.code;
        const atkCode = atkMetric?.code;
        const defCode = defMetric?.code;
        const metricCodes = ["rounds", pistolCode, atkCode, defCode].filter((code): code is string => Boolean(code));

        roundRequest({}, metricCodes)
            .then((roundRows) => {
                if (cancelled) return;
                patchRates({
                    pistol: pistolCode
                        ? mapRateStatsFromMetric(roundRows, pistolCode, PISTOL_COUNT_KEYS)
                        : new Map<string, MapRateStat>(),
                    atk: atkCode
                        ? mapRateStatsFromMetric(roundRows, atkCode, ["atk_rounds", "attack_rounds", "rounds_atk"])
                        : new Map<string, MapRateStat>(),
                    def: defCode
                        ? mapRateStatsFromMetric(roundRows, defCode, ["def_rounds", "defense_rounds", "rounds_def"])
                        : new Map<string, MapRateStat>(),
                });
            })
            .catch(() => undefined)
            .finally(() => {
                if (!cancelled) persist({ roundsDone: true });
            });
        }

        return () => {
            cancelled = true;
        };
    }, [open, puuid, accountId]);

    return (
        <Box sx={{ display: "flex", alignItems: "stretch", minHeight: EXPAND_BODY_MIN_HEIGHT }}>
            <AgentExpandPortrait
                agent={mainAgent || agents[0]?.id}
                footer={
                    playerId || vlrUrl || trackerUrl ? (
                        <SplashProfileLinks playerId={playerId} vlrUrl={vlrUrl} trackerUrl={trackerUrl} />
                    ) : undefined
                }
            />
            <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0, minHeight: EXPAND_BODY_MIN_HEIGHT, px: 1.5, pb: 1.25, pt: 0.75 }}>
                {!playerId ? (
                    <LinkPlayerSection puuid={puuid} accountName={accountName || ""} onLinked={onLinked} />
                ) : null}
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ alignItems: "flex-start" }}>
                    <Box sx={{ flex: 1, minWidth: 0, width: "100%", minHeight: EXPAND_AGENT_TABLE_HEIGHT }}>
                        <Reveal show={agentsReady}>
                            <ExpandStatsTable rows={agents} kind="agent" fixedRows={AGENT_TABLE_ROWS} />
                        </Reveal>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, width: "100%", minHeight: EXPAND_AGENT_TABLE_HEIGHT }}>
                        <Reveal show={rolesReady}>
                            {roles.length ? (
                                <ExpandStatsTable rows={roles} kind="role" />
                            ) : (
                                <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.35)", py: 1 }}>No role stats.</Typography>
                            )}
                        </Reveal>
                    </Box>
                </Stack>
                <MapStatsStrip maps={maps} ready={mapsReady} mapsById={mapsById} />
            </Stack>
        </Box>
    );
}

export default function SoloQLeaderboard() {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    const ctx = useReferentialContext();
    const scale = useScale();

    const [rows, setRows] = useState<LeaderboardRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [region, setRegion] = useState("eu");
    const [rankBandId, setRankBandId] = useState(RANK_BANDS[0].id);
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
    const [minGames, setMinGames] = useState(0);
    const [page, setPage] = useState(0);
    const [sortKey, setSortKey] = useState<SortKey>("rank");
    const [sortDir, setSortDir] = useState<SortDir>("desc");
    const [expandedPuuid, setExpandedPuuid] = useState<string | null>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const expandedPuuidRef = useRef<string | null>(null);

    const rankBand = RANK_BANDS.find((band) => band.id === rankBandId) || RANK_BANDS[0];
    const agentFilter = useMemo(
        () => resolvedAgentFilter(selectedAgents, selectedRoles, ctx.agentsReferential),
        [selectedAgents, selectedRoles, ctx.agentsReferential],
    );
    const agentFilterKey =
        agentFilter === undefined ? "pending" : agentFilter === null ? "all" : [...agentFilter].sort((a, b) => a.localeCompare(b)).join(",");
    const visibleAgents = useMemo(() => {
        const list = selectedRoles.length
            ? agentsMatchingRoles(ctx.agentsReferential, selectedRoles)
            : ctx.agentsReferential;
        return [...list].sort((a, b) => a.id.localeCompare(b.id));
    }, [ctx.agentsReferential, selectedRoles]);

    const playersById = useMemo(() => {
        const byId = new Map<string, Player>();
        for (const player of ctx.playersReferential) byId.set(player.id.toLowerCase(), player);
        return byId;
    }, [ctx.playersReferential]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setPage(0);
        setSortKey("rank");
        setSortDir("desc");
        setExpandedPuuid(null);

        if (agentFilter === undefined) return;
        if (agentFilter && agentFilter.length === 0) {
            setRows([]);
            setLoading(false);
            return;
        }

        const additional_filters: Record<string, string | number> = {
            account__server: region,
            account__rank_tier__gte: rankBand.min,
        };
        if (rankBand.max != null) additional_filters.account__rank_tier__lt = rankBand.max;
        if (agentFilter?.length) additional_filters.agent__in = agentFilter.join(",");

        const accountFilters: Record<string, string | number> = {
            server: region,
            rank_tier__gte: rankBand.min,
        };
        if (rankBand.max != null) accountFilters.rank_tier__lt = rankBand.max;

        const agentsMetric = ctx.metricsReferential.find((metric) => metric.code.toLowerCase() === "agents");

        Promise.all([
            ctx.reckonApi.soloqGameSummaries.aggregate({
                groupby: "puuid" as any,
                ordering: ["-account__rank_tier", "-account__ranked_rating"] as any,
                aggregates: [STAT_AGGREGATES.join(",")] as any,
                additional_filters,
                metrics: [(agentsMetric?.code || "agents")] as any,
            }),
            ctx.reckonApi.soloQAccounts.list({
                ordering: ["-rank_tier", "-ranked_rating"],
                additional_filters: accountFilters,
            }).catch(() => [] as SoloQAccounts[]),
        ])
            .then(([result, accountsResult]) => {
                if (cancelled) return;
                const accountsByPuuid = new Map<string, SoloQAccounts>();
                for (const account of asAccountList(accountsResult)) {
                    if (account.puuid) accountsByPuuid.set(account.puuid, account);
                }
                const parsed = uniqueByPuuid(
                    asAggregateRows(result, "puuid")
                        .map(fromAggregateRow)
                        .filter((row): row is LeaderboardRow => Boolean(row))
                        .map((row) => applyAccount(row, accountsByPuuid.get(row.puuid))),
                );
                setRows(parsed);
                setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setRows([]);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [region, rankBand, agentFilterKey]);

    const filteredRows = useMemo(
        () => (minGames > 0 ? rows.filter((row) => row.games >= minGames) : rows),
        [rows, minGames],
    );
    const sortedRows = useMemo(() => sortLeaderboard(filteredRows, sortKey, sortDir), [filteredRows, sortKey, sortDir]);
    const pageCount = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
    const currentPage = Math.min(page, pageCount - 1);
    const pageStart = currentPage * PAGE_SIZE;
    const pageRows = sortedRows.slice(pageStart, pageStart + PAGE_SIZE);

    useEffect(() => {
        setPage(0);
        setExpandedPuuid(null);
    }, [minGames]);

    useEffect(() => {
        listRef.current?.scrollTo({ top: 0 });
    }, [currentPage]);

    const toggleSort = (key: SortKey) => {
        setPage(0);
        setExpandedPuuid(null);
        if (sortKey === key) {
            setSortDir((prev) => (prev === "desc" ? "asc" : "desc"));
            return;
        }
        setSortKey(key);
        setSortDir("desc");
    };

    const scrollExpandedIntoView = (puuid: string, collapsingPuuid: string | null) => {
        const container = listRef.current;
        const block = rowRefs.current[puuid];
        if (!container || !block) return;
        const headerH = headerRef.current?.offsetHeight ?? 0;
        const visibleH = Math.max(0, container.clientHeight - headerH);
        const rowH = (block.firstElementChild as HTMLElement | null)?.offsetHeight ?? 52;
        const expandInner = block.querySelector(".MuiCollapse-wrapperInner") as HTMLElement | null;
        const expandH = Math.max(expandInner?.scrollHeight ?? 0, EXPAND_BODY_MIN_HEIGHT);
        const blockH = rowH + expandH;
        let blockTop =
            container.scrollTop + (block.getBoundingClientRect().top - container.getBoundingClientRect().top);
        if (collapsingPuuid && collapsingPuuid !== puuid) {
            const previous = rowRefs.current[collapsingPuuid];
            if (previous && previous.getBoundingClientRect().top < block.getBoundingClientRect().top) {
                const previousInner = previous.querySelector(".MuiCollapse-wrapperInner") as HTMLElement | null;
                blockTop -= Math.max(previousInner?.scrollHeight ?? 0, EXPAND_BODY_MIN_HEIGHT);
            }
        }
        const topGap = blockH >= visibleH ? 8 : Math.max(8, (visibleH - blockH) / 2);
        container.scrollTo({ top: Math.max(0, blockTop - headerH - topGap), behavior: "smooth" });
    };

    useLayoutEffect(() => {
        const next = expandedPuuid;
        const previous = expandedPuuidRef.current;
        expandedPuuidRef.current = next;
        if (!next) return;
        const collapsing = previous && previous !== next ? previous : null;
        let frame2 = 0;
        const frame1 = requestAnimationFrame(() => {
            frame2 = requestAnimationFrame(() => scrollExpandedIntoView(next, collapsing));
        });
        return () => {
            cancelAnimationFrame(frame1);
            cancelAnimationFrame(frame2);
        };
    }, [expandedPuuid]);

    return (
        <WindowContainer
            spacing={2}
            sx={{
                height: "100%",
                minHeight: 0,
                overflow: "hidden",
                pb: 2,
            }}
        >
            <Box sx={{ minWidth: 0, flexShrink: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
                    SoloQ leaderboard
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {filteredRows.length
                        ? `${filteredRows.length} ${rankBand.label.toLowerCase()} account${filteredRows.length === 1 ? "" : "s"} · ${region}`
                        : `${rankBand.label} · ${region}`}
                </Typography>
            </Box>

            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                sx={{ flex: 1, minHeight: 0, alignItems: "stretch" }}
            >
                <NeonCard
                    sx={{
                        width: { xs: "100%", md: 280 },
                        flexShrink: 0,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                    }}
                >
                    <NeonCardHeader title="Filters" sx={{ minHeight: "auto", py: 1.25, flexShrink: 0 }} />
                    <NeonCardContent sx={{ pt: 0, pb: "16px !important", flex: 1, minHeight: 0, ...scrollable }}>
                        <Stack spacing={2.25}>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: "0.62rem",
                                        fontWeight: 700,
                                        letterSpacing: "0.1em",
                                        textTransform: "uppercase",
                                        color: "rgba(255,255,255,0.42)",
                                        mb: 0.9,
                                    }}
                                >
                                    Region
                                </Typography>
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: 0.5,
                                        p: 0.5,
                                        borderRadius: 1,
                                        background: "rgba(255,255,255,0.03)",
                                        border: "1px solid rgba(255,255,255,0.08)",
                                    }}
                                >
                                    {REGIONS.map((value) => {
                                        const selected = region === value;
                                        return (
                                            <Box
                                                key={value}
                                                onClick={() => setRegion(value)}
                                                sx={{
                                                    py: 0.85,
                                                    px: 0.5,
                                                    borderRadius: 0.75,
                                                    textAlign: "center",
                                                    cursor: "pointer",
                                                    fontSize: "0.72rem",
                                                    fontWeight: 800,
                                                    letterSpacing: "0.08em",
                                                    textTransform: "uppercase",
                                                    color: selected ? "#fff" : "rgba(255,255,255,0.52)",
                                                    background: selected ? `${neonColor}22` : "transparent",
                                                    border: selected ? `1px solid ${neonColor}70` : "1px solid transparent",
                                                    boxShadow: selected ? `inset 0 0 12px ${neonColor}18, 0 0 12px ${neonColor}14` : "none",
                                                    transition: "all 0.18s ease",
                                                    "&:hover": {
                                                        color: "#fff",
                                                        background: selected ? `${neonColor}28` : "rgba(255,255,255,0.06)",
                                                    },
                                                }}
                                            >
                                                {value}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: "0.62rem",
                                        fontWeight: 700,
                                        letterSpacing: "0.1em",
                                        textTransform: "uppercase",
                                        color: "rgba(255,255,255,0.42)",
                                        mb: 0.9,
                                    }}
                                >
                                    Rank
                                </Typography>
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: 0.5,
                                        p: 0.5,
                                        borderRadius: 1,
                                        background: "rgba(255,255,255,0.03)",
                                        border: "1px solid rgba(255,255,255,0.08)",
                                    }}
                                >
                                    {RANK_BANDS.map((band) => {
                                        const selected = rankBandId === band.id;
                                        const tier = findCompetitiveTier(ctx.competitiveTiersReferential, band.min);
                                        const palette = rankPalette(tier, band.min);
                                        const accent = palette.accent;
                                        return (
                                            <Box
                                                key={band.id}
                                                onClick={() => setRankBandId(band.id)}
                                                sx={{
                                                    py: 1,
                                                    px: 0.5,
                                                    borderRadius: 0.75,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    gap: 0.4,
                                                    cursor: "pointer",
                                                    color: selected ? "#fff" : "rgba(255,255,255,0.62)",
                                                    background: selected
                                                        ? `linear-gradient(180deg, ${palette.wash} 0%, transparent 100%)`
                                                        : "transparent",
                                                    border: selected ? `1px solid ${accent}` : "1px solid transparent",
                                                    boxShadow: selected ? `inset 0 0 12px ${palette.wash}, 0 0 10px ${palette.wash}` : "none",
                                                    transition: "all 0.18s ease",
                                                    "&:hover": {
                                                        color: "#fff",
                                                        background: selected ? palette.wash : "rgba(255,255,255,0.06)",
                                                    },
                                                }}
                                            >
                                                <RankIcon tier={band.min} size={28} />
                                                <Typography sx={{ fontWeight: 800, fontSize: "0.68rem", letterSpacing: "0.04em" }}>
                                                    {band.label}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                            <Box>
                                <Typography sx={FILTER_LABEL_SX}>Role</Typography>
                                <Box sx={FILTER_PANEL_SX}>
                                    {ROLE_FILTERS.map((role) => {
                                        const selected = selectedRoles.includes(role);
                                        const icon = ctx.agentsReferential.find(
                                            (agent) => formatRoleName(agent.role).toLowerCase() === role.toLowerCase(),
                                        )?.role_icon;
                                        return (
                                            <Box
                                                key={role}
                                                onClick={() => {
                                                    const next = toggleFilterValue(selectedRoles, role);
                                                    setSelectedRoles(next);
                                                    if (next.length) {
                                                        const allowed = new Set(
                                                            agentsMatchingRoles(ctx.agentsReferential, next).map((agent) =>
                                                                agent.id.toLowerCase(),
                                                            ),
                                                        );
                                                        setSelectedAgents((prev) =>
                                                            prev.filter((id) => allowed.has(id.toLowerCase())),
                                                        );
                                                    }
                                                }}
                                                sx={{
                                                    py: 0.85,
                                                    px: 0.5,
                                                    borderRadius: 0.75,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    gap: 0.35,
                                                    cursor: "pointer",
                                                    color: selected ? "#fff" : "rgba(255,255,255,0.62)",
                                                    background: selected ? `${neonColor}22` : "transparent",
                                                    border: selected ? `1px solid ${neonColor}70` : "1px solid transparent",
                                                    boxShadow: selected ? `inset 0 0 12px ${neonColor}18, 0 0 12px ${neonColor}14` : "none",
                                                    transition: "all 0.18s ease",
                                                    "&:hover": {
                                                        color: "#fff",
                                                        background: selected ? `${neonColor}28` : "rgba(255,255,255,0.06)",
                                                    },
                                                }}
                                            >
                                                <RoleIcon role={role} icon={icon} size={22} />
                                                <Typography sx={{ fontWeight: 800, fontSize: "0.62rem", letterSpacing: "0.04em" }}>
                                                    {role}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                            <Box>
                                <Typography sx={FILTER_LABEL_SX}>Agents</Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                        p: 0.5,
                                        borderRadius: 1,
                                        background: "rgba(255,255,255,0.03)",
                                        border: "1px solid rgba(255,255,255,0.08)",
                                    }}
                                >
                                    {visibleAgents.map((agent) => {
                                        const selected = selectedAgents.includes(agent.id);
                                        return (
                                            <Box
                                                key={agent.id}
                                                onClick={() => setSelectedAgents((prev) => toggleFilterValue(prev, agent.id))}
                                                sx={{
                                                    width: 34,
                                                    height: 34,
                                                    borderRadius: 0.75,
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    opacity: selected || selectedAgents.length === 0 ? 1 : 0.42,
                                                    background: selected ? `${neonColor}22` : "transparent",
                                                    border: selected ? `1px solid ${neonColor}80` : "1px solid transparent",
                                                    boxShadow: selected ? `0 0 10px ${neonColor}22` : "none",
                                                    transition: "all 0.16s ease",
                                                    "&:hover": { opacity: 1, background: "rgba(255,255,255,0.06)" },
                                                }}
                                            >
                                                <AgentIcon agent_name={agent.id} sx={{ width: 28, height: 28 }} />
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                            <Box>
                                <Stack direction="row" sx={{ alignItems: "baseline", justifyContent: "space-between", mb: 0.4 }}>
                                    <Typography sx={{ ...FILTER_LABEL_SX, mb: 0 }}>Min games</Typography>
                                    <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, color: "#fff" }}>
                                        {minGames}
                                    </Typography>
                                </Stack>
                                <Slider
                                    size="small"
                                    value={minGames}
                                    min={0}
                                    max={MIN_GAMES_MAX}
                                    step={5}
                                    onChange={(_event, value) => setMinGames(Array.isArray(value) ? value[0] : value)}
                                    sx={{
                                        color: neonColor,
                                        mt: 0.5,
                                        ml: 0.5,
                                        width: "calc(100% - 8px)",
                                        "& .MuiSlider-thumb": {
                                            width: 14,
                                            height: 14,
                                            boxShadow: `0 0 8px ${neonColor}80`,
                                        },
                                        "& .MuiSlider-rail": { opacity: 0.28 },
                                    }}
                                />
                            </Box>
                        </Stack>
                    </NeonCardContent>
                </NeonCard>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10, flex: 1 }}>
                    <CircularProgress sx={{ color: neonColor }} />
                </Box>
            ) : (
                <NeonCard
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        pb: 3,
                    }}
                >
                    <NeonCardHeader
                        title="Standings"
                        sx={{ minHeight: "auto", py: 1.25, flexShrink: 0 }}
                        action={
                            sortedRows.length > 0 ? (
                                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", mr: 0.5 }}>
                                    <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.55)", mr: 0.5 }}>
                                        {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, sortedRows.length)} of {sortedRows.length}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        disabled={currentPage <= 0}
                                        onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                                        sx={{
                                            color: "rgba(255,255,255,0.72)",
                                            "&.Mui-disabled": { color: "rgba(255,255,255,0.18)" },
                                        }}
                                    >
                                        <KeyboardArrowLeft />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        disabled={currentPage >= pageCount - 1}
                                        onClick={() => setPage((prev) => Math.min(pageCount - 1, prev + 1))}
                                        sx={{
                                            color: "rgba(255,255,255,0.72)",
                                            "&.Mui-disabled": { color: "rgba(255,255,255,0.18)" },
                                        }}
                                    >
                                        <KeyboardArrowRight />
                                    </IconButton>
                                </Stack>
                            ) : null
                        }
                    />
                    <NeonCardContent
                        ref={listRef}
                        sx={{
                            pt: 0,
                            pb: "24px !important",
                            flex: 1,
                            minHeight: 0,
                            ...scrollable,
                        }}
                    >
                        {sortedRows.length === 0 ? (
                            <NeonEmptyState
                                message={
                                    minGames > 0 || selectedAgents.length > 0 || selectedRoles.length > 0
                                        ? `No ${rankBand.label} accounts match these filters for ${region}.`
                                        : `No ${rankBand.label} accounts found for ${region}.`
                                }
                                height="240px"
                            />
                        ) : (
                            <NeonTableContainer sx={{ pb: expandedPuuid ? `${EXPAND_BODY_MIN_HEIGHT + 96}px` : 3 }}>
                                <Box ref={headerRef}>
                                <NeonTableHeader>
                                    <Box sx={{ width: FLAG_LAYOUT_WIDTH, minWidth: FLAG_LAYOUT_WIDTH, flexShrink: 0, ml: "-16px" }} />
                                    <NeonTableHeaderCell width="44px">#</NeonTableHeaderCell>
                                    <NeonTableHeaderCell flex={1.4} minWidth="220px" align="left">
                                        Player
                                    </NeonTableHeaderCell>
                                    <NeonTableHeaderCell width={`${AGENTS_COLUMN_WIDTH}px`}>
                                        Agents
                                    </NeonTableHeaderCell>
                                    <SortHeader
                                        id="rank"
                                        label="Rank"
                                        width="148px"
                                        sortKey={sortKey}
                                        sortDir={sortDir}
                                        onSort={toggleSort}
                                    />
                                    <SortHeader id="games" label="G" width="48px" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                                    <SortHeader id="winRate" label="WR" width="64px" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                                    <SortHeader id="acs" label="ACS" width="56px" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                                    <SortHeader id="kd" label="KD" width="52px" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                                    <SortHeader id="adr" label="ADR" width="56px" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                                    <SortHeader id="rating" label="Rating" width="64px" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                                </NeonTableHeader>
                                </Box>
                                {pageRows.map((row, index) => {
                                    const player = row.playerId ? playersById.get(row.playerId.toLowerCase()) || null : null;
                                    const tier = findCompetitiveTier(ctx.competitiveTiersReferential, row.rankTier);
                                    const place = pageStart + index + 1;
                                    const ellipsis = {
                                        whiteSpace: "nowrap" as const,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        minWidth: 0,
                                    };
                                    const wrColor = scale.color(row.winRate, SCALE_STOPS.rate);
                                    const acsColor = scale.color(row.acs, SCALE_STOPS.acs);
                                    const kdColor = scale.color(row.kd, SCALE_STOPS.kd);
                                    const adrColor = scale.color(row.adr, SCALE_STOPS.adr);
                                    const ratingColor = scale.color(row.rating || null, SCALE_STOPS.rating);
                                    const accent = agentAccent(row.agents[0]);
                                    const playedRole = mostPlayedRole(row.agents, ctx.agentsReferential);
                                    const expanded = expandedPuuid === row.puuid;
                                    return (
                                        <Box
                                            key={row.puuid}
                                            ref={(node) => {
                                                rowRefs.current[row.puuid] = node as HTMLDivElement | null;
                                            }}
                                            sx={{ width: "100%" }}
                                        >
                                        <NeonTableRow
                                            onClick={() => {
                                                setExpandedPuuid(expanded ? null : row.puuid);
                                            }}
                                            isSelected={expanded}
                                            animationDelay={Math.min(index, 12)}
                                            sx={{
                                                overflow: "hidden",
                                                borderRadius: expanded ? "6px 6px 0 0" : "6px",
                                                ...agentRowSx(accent),
                                            }}
                                        >
                                            <PlayedRole
                                                role={playedRole?.role}
                                                icon={playedRole?.icon}
                                                nationality={player?.nationality}
                                                accent={accent}
                                                seed={row.puuid}
                                            />
                                            <NeonTableCell
                                                width={`${FLAG_LAYOUT_WIDTH}px`}
                                                align="left"
                                                sx={{
                                                    alignSelf: "stretch",
                                                    position: "relative",
                                                    overflow: "hidden",
                                                    my: "-8px",
                                                    ml: "-16px",
                                                    zIndex: 1,
                                                }}
                                            >
                                                {null}
                                            </NeonTableCell>
                                            <NeonTableCell width="44px" sx={{ position: "relative", zIndex: 1 }}>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 800,
                                                        fontSize: "0.78rem",
                                                        color: "rgba(255,255,255,0.7)",
                                                        textShadow: "0 1px 6px rgba(0,0,0,0.85)",
                                                    }}
                                                >
                                                    {place}
                                                </Typography>
                                            </NeonTableCell>
                                            <NeonTableCell flex={1.4} minWidth="220px" align="left">
                                                <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", minWidth: 0, width: "100%" }}>
                                                    <Box
                                                        sx={{
                                                            width: PLAYER_ORGS_SLOT_WIDTH,
                                                            minWidth: PLAYER_ORGS_SLOT_WIDTH,
                                                            height: ROW_PLAYER_ORG_SIZE,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "flex-start",
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {player ? (
                                                            <PlayerOrgs
                                                                player={player}
                                                                size={ROW_PLAYER_ORG_SIZE}
                                                                showFlag={false}
                                                                showLastTeam={false}
                                                                showOtherTeams={false}
                                                            />
                                                        ) : null}
                                                    </Box>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        {row.playerId ? (
                                                            <>
                                                                <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, lineHeight: 1.15, ...ellipsis }}>
                                                                    {row.playerId}
                                                                </Typography>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "0.58rem",
                                                                        fontWeight: 600,
                                                                        color: "rgba(255,255,255,0.45)",
                                                                        lineHeight: 1.2,
                                                                        ...ellipsis,
                                                                    }}
                                                                >
                                                                    {row.accountName}
                                                                </Typography>
                                                            </>
                                                        ) : (
                                                            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, ...ellipsis }}>
                                                                {row.accountName}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Stack>
                                            </NeonTableCell>
                                            <NeonTableCell
                                                width={`${AGENTS_COLUMN_WIDTH}px`}
                                                sx={{
                                                    alignSelf: "stretch",
                                                    position: "relative",
                                                    overflow: "hidden",
                                                    my: "-8px",
                                                }}
                                            >
                                                <PlayedAgents agents={row.agents} />
                                            </NeonTableCell>
                                            <NeonTableCell width="148px">
                                                <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", justifyContent: "center", minWidth: 0 }}>
                                                    <Box
                                                        sx={{
                                                            width: RANK_ICON_SIZE,
                                                            minWidth: RANK_ICON_SIZE,
                                                            height: RANK_ICON_SIZE,
                                                            flexShrink: 0,
                                                            overflow: "hidden",
                                                            maskImage: RANK_LEFT_DISSOLVE,
                                                            WebkitMaskImage: RANK_LEFT_DISSOLVE,
                                                            maskRepeat: "no-repeat",
                                                            WebkitMaskRepeat: "no-repeat",
                                                            maskSize: "100% 100%",
                                                            WebkitMaskSize: "100% 100%",
                                                        }}
                                                    >
                                                        <RankIcon
                                                            raw
                                                            tier={row.rankTier}
                                                            rating={row.rankedRating}
                                                            size={RANK_ICON_SIZE}
                                                        />
                                                    </Box>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography sx={{ fontWeight: 700, fontSize: "0.74rem", lineHeight: 1.15, ...ellipsis }}>
                                                            {rankLabel(tier) || fullRankFromNumber(typeof row.rankTier === "number" ? row.rankTier : null) || String(row.rankTier || rankBand.label)}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: "0.58rem",
                                                                fontWeight: 600,
                                                                color: "rgba(255,255,255,0.45)",
                                                                lineHeight: 1.2,
                                                                fontVariantNumeric: "tabular-nums",
                                                            }}
                                                        >
                                                            {row.rankedRating != null ? `${row.rankedRating} RR` : "—"}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </NeonTableCell>
                                            <NeonTableCell width="48px">
                                                <Typography sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{row.games}</Typography>
                                            </NeonTableCell>
                                            <NeonTableCell width="64px">
                                                <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", ...scale.glow(wrColor) }}>
                                                    {formatters.percent(row.winRate)}
                                                </Typography>
                                            </NeonTableCell>
                                            <NeonTableCell width="56px">
                                                <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", ...scale.glow(acsColor) }}>
                                                    {formatters.integer(row.acs)}
                                                </Typography>
                                            </NeonTableCell>
                                            <NeonTableCell width="52px">
                                                <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", ...scale.glow(kdColor) }}>
                                                    {formatters.float2(row.kd)}
                                                </Typography>
                                            </NeonTableCell>
                                            <NeonTableCell width="56px">
                                                <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", ...scale.glow(adrColor) }}>
                                                    {formatters.integer(row.adr)}
                                                </Typography>
                                            </NeonTableCell>
                                            <NeonTableCell width="64px">
                                                <Typography sx={{ fontWeight: 800, fontSize: "0.8rem", ...scale.glow(ratingColor) }}>
                                                    {row.rating ? formatters.float2(row.rating) : "—"}
                                                </Typography>
                                            </NeonTableCell>
                                        </NeonTableRow>
                                        <Collapse in={expanded} unmountOnExit timeout={REVEAL_MS} easing={{ enter: REVEAL_EASE, exit: "ease-in" }}>
                                            <Box
                                                sx={{
                                                    mb: 0.5,
                                                    borderRadius: "0 0 6px 6px",
                                                    overflow: "hidden",
                                                    background: accent
                                                        ? `linear-gradient(90deg, ${hexRgba(accent, 0.16)} 0%, ${hexRgba(accent, 0.06)} 100%)`
                                                        : "rgba(255,255,255,0.03)",
                                                    border: accent
                                                        ? `1px solid ${hexRgba(accent, 0.22)}`
                                                        : "1px solid rgba(255,255,255,0.08)",
                                                    borderTop: "none",
                                                }}
                                            >
                                                <PlayerBreakdown
                                                    puuid={row.puuid}
                                                    accountId={row.accountId}
                                                    open={expanded}
                                                    playerId={row.playerId}
                                                    accountName={row.accountName}
                                                    mainAgent={row.agents[0]}
                                                    onLinked={(playerId) => {
                                                        setRows((prev) =>
                                                            prev.map((entry) =>
                                                                entry.puuid === row.puuid ? { ...entry, playerId } : entry,
                                                            ),
                                                        );
                                                    }}
                                                />
                                            </Box>
                                        </Collapse>
                                        </Box>
                                    );
                                })}
                            </NeonTableContainer>
                        )}
                    </NeonCardContent>
                </NeonCard>
            )}
            </Stack>
        </WindowContainer>
    );
}
